import { Chip, type ChipProps, type TypeText } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { type ComponentType } from 'react';

type TextColor = Exclude<keyof TypeText, '_states'>;
type TextColors = `text.${TextColor}`;
export type ChipPillColors = Exclude<NonNullable<ChipProps['color']>, 'default'> | TextColors;

const isTextColor = (color: string): color is TextColors =>
  ['text.primary', 'text.secondary', 'text.disabled'].includes(color);

const isMuiColor = (color: string): color is ChipPillColors =>
  ['primary', 'secondary', 'error', 'info', 'warning', 'success'].includes(color);

const StyledChip = styled(Chip as ComponentType<ChipPillProps>, {
  shouldForwardProp: (prop) => prop !== 'textTransform' && prop !== 'color',
})<ChipPillProps>(({ theme, color = 'primary', textTransform = 'uppercase' }) => {
  let backgroundColor: string | undefined;
  let colorName: string | undefined;

  if (isTextColor(color)) {
    const textColor = color.split('.')[1] as TextColor;
    const selectedColor = theme.palette.text[textColor];
    backgroundColor = theme.palette.text._states.selected;
    colorName = selectedColor;
  } else if (isMuiColor(color)) {
    const selectedColor = theme.palette[color];
    backgroundColor = selectedColor?._states?.selected;
    colorName = selectedColor?.main;
  }

  return css`
    ${backgroundColor && `background-color: ${backgroundColor};`}
    ${colorName && `color: ${colorName};`}
    text-transform: ${textTransform};
    font-weight: 500;
  `;
});

export type ChipPillProps = Omit<ChipProps, 'color' | 'size'> & {
  color?: ChipPillColors;
  size?: ChipProps['size'];
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
};

export const ChipPill = ({ size = 'small', variant = 'filled', ...props }: ChipPillProps) => (
  <StyledChip size={size} variant={variant} {...props} />
);
